import { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useMountedRef } from 'hooks/useMountRef';

function getInitialExpandedIdsFromUrl(allIds: string[]): Set<string> {
  if (typeof window === 'undefined') {
    return new Set();
  }

  const pathname = window.location.pathname;
  const idsInPathname = allIds.filter((id) => pathname.includes(id));

  return new Set(idsInPathname);
}

export interface UseAccordionsExpansionOptions {
  /** Map checklistItemId from location.state (return from issue) to the id used in expandedIds. Omit when they are the same (e.g. Form). */
  resolveReturnChecklistItemId?: (checklistItemId: string) => string | undefined;
  /** IDs that should be expanded on first render (before user interaction). */
  initialExpandedIds?: string[];
  /** When true, all nodes in allIds start expanded and newly added IDs stay expanded until the user changes expansion manually. */
  defaultExpandAll?: boolean;
}

export interface OnItemExpandedChangeOptions {
  /** When false, expansion change does not disable defaultExpandAll for late-loaded IDs. */
  userInitiated?: boolean;
}

export function useAccordionsExpansion(
  allIds: string[],
  options?: UseAccordionsExpansionOptions
): {
  expandedIds: Set<string>;
  isExpanded: (id: string) => boolean;
  allExpanded: boolean;
  setAllExpanded: (expand: boolean) => void;
  toggleExpandAll: () => void;
  onItemExpandedChange: (
    id: string,
    expanded: boolean,
    changeOptions?: OnItemExpandedChangeOptions
  ) => void;
  isExpandingAll: boolean;
} {
  const location = useLocation();
  const defaultExpandAll = options?.defaultExpandAll ?? false;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    new Set([
      ...(defaultExpandAll
        ? allIds
        : options?.initialExpandedIds || []),
      ...Array.from(getInitialExpandedIdsFromUrl(allIds)),
    ])
  );
  const isExpandingAllRef = useRef<boolean>(false);
  const appliedReturnStateRef = useRef<string | null>(null);
  const appliedInitialExpandedIdsRef = useRef<boolean>(false);
  const userTouchedExpansionRef = useRef<boolean>(false);
  const mountedRef = useMountedRef();

  useEffect(() => {
    if (defaultExpandAll || appliedInitialExpandedIdsRef.current) {
      return;
    }

    const initialExpandedIds = options?.initialExpandedIds || [];
    const idsToApply = initialExpandedIds.filter((id) => allIds.includes(id));

    if (idsToApply.length === 0) {
      return;
    }

    setExpandedIds((prev) => {
      const next = new Set(prev);
      idsToApply.forEach((id) => {
        next.add(id);
      });
      return next;
    });

    appliedInitialExpandedIdsRef.current = true;
  }, [allIds, defaultExpandAll, options?.initialExpandedIds]);

  // Reconciles the user's expansion with a changing list of ids (tied to the effects around it).
  useEffect(() => {
    setExpandedIds((prev) => {
      const next = new Set<string>();
      Array.from(prev).forEach((id) => {
        if (allIds.includes(id)) {
          next.add(id);
        }
      });

      if (defaultExpandAll && !userTouchedExpansionRef.current) {
        allIds.forEach((id) => {
          next.add(id);
        });
      }

      return next;
    });
  }, [allIds, defaultExpandAll]);

  useEffect(() => {
    if (isExpandingAllRef.current) {
      const expectedSize =
        expandedIds.size === 0 || expandedIds.size === allIds.length;
      if (expectedSize) {
        requestAnimationFrame(() => {
          isExpandingAllRef.current = false;
        });
      }
    }
  }, [expandedIds, allIds.length]);

  const allExpanded =
    expandedIds.size > 0 && expandedIds.size === allIds.length;

  const setAllExpanded = useCallback(
    (expand: boolean) => {
      userTouchedExpansionRef.current = true;
      setExpandedIds(expand ? new Set(allIds) : new Set());
    },
    [allIds]
  );

  const toggleExpandAll = useCallback(() => {
    userTouchedExpansionRef.current = true;
    isExpandingAllRef.current = true;
    setExpandedIds((prev) =>
      prev.size === allIds.length ? new Set() : new Set(allIds)
    );
  }, [allIds]);

  const onItemExpandedChange = useCallback(
    (
      id: string,
      expanded: boolean,
      changeOptions?: OnItemExpandedChangeOptions
    ) => {
      if (changeOptions?.userInitiated !== false) {
        userTouchedExpansionRef.current = true;
      }
      isExpandingAllRef.current = false;
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (expanded) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (!mountedRef.current) return;

    const state = location.state as {
      checklistItemId?: string;
      allExpanded?: boolean;
    } | null;
    const checklistItemId = state?.checklistItemId;
    const returnAllExpanded = state?.allExpanded === true;
    const key =
      checklistItemId != null || returnAllExpanded
        ? `${checklistItemId ?? ''}-${returnAllExpanded}`
        : null;

    if (key == null || key === appliedReturnStateRef.current) return;
    appliedReturnStateRef.current = key;

    if (returnAllExpanded) {
      // Programmatic restore when navigating back from an issue with allExpanded state.
      // Intentionally skips setAllExpanded (and userTouchedExpansionRef), same as deep-links
      // with userInitiated: false — defaultExpandAll keeps auto-expanding late-loaded IDs.
      setExpandedIds(new Set(allIds));
      return;
    }
    if (!checklistItemId) return;

    const expandedId = options?.resolveReturnChecklistItemId
      ? options.resolveReturnChecklistItemId(checklistItemId)
      : checklistItemId;
    if (expandedId == null || expandedIds.has(expandedId)) return;

    onItemExpandedChange(expandedId, true, { userInitiated: false });
  }, [
    allIds,
    location.state,
    options?.resolveReturnChecklistItemId,
    expandedIds,
    onItemExpandedChange,
  ]);

  const isExpanded = useCallback(
    (id: string) => expandedIds.has(id),
    [expandedIds]
  );

  return {
    expandedIds,
    isExpanded,
    allExpanded,
    setAllExpanded,
    toggleExpandAll,
    onItemExpandedChange,
    isExpandingAll: isExpandingAllRef.current,
  };
}
